import { useAppUpdate } from '../hooks/useAppUpdate';

/**
 * Update notification banner
 *
 * Appears at the top of the screen when a new version is available.
 * - Non-intrusive for regular updates (can be dismissed)
 * - Unmissable for force updates (can't be dismissed)
 * - One-click update button clears caches and reloads
 */
export default function UpdateBanner() {
  const {
    updateAvailable,
    newVersion,
    releaseNotes,
    forceUpdate,
    updating,
    performUpdate,
    dismissUpdate,
  } = useAppUpdate();

  if (!updateAvailable) {
    return null;
  }

  return (
    <div
      className={`fixed top-0 left-0 right-0 z-[9999] px-4 py-3 shadow-lg ${
        forceUpdate
          ? 'bg-gradient-to-r from-red-600 to-red-700'
          : 'bg-gradient-to-r from-blue-600 to-purple-600'
      }`}
    >
      <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          {/* Update icon */}
          <div className="flex-shrink-0">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6 text-gray-900"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
          </div>

          {/* Message */}
          <div className="text-gray-900 min-w-0">
            <div className="font-semibold text-sm">
              {forceUpdate ? 'Critical Update Required' : 'New Update Available'}
            </div>
            <div className="text-xs text-gray-900/80 truncate">
              {releaseNotes || `Version ${newVersion}`}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 flex-shrink-0">
          {!forceUpdate && (
            <button
              onClick={dismissUpdate}
              className="px-3 py-1.5 text-xs text-gray-900/80 hover:text-gray-900 transition-colors"
              disabled={updating}
            >
              Later
            </button>
          )}

          <button
            onClick={performUpdate}
            disabled={updating}
            className={`px-4 py-1.5 text-sm font-medium rounded-md transition-all ${
              forceUpdate
                ? 'bg-white text-red-600 hover:bg-red-50'
                : 'bg-white text-blue-600 hover:bg-blue-50'
            } ${updating ? 'opacity-75 cursor-wait' : ''}`}
          >
            {updating ? (
              <span className="flex items-center gap-2">
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Updating...
              </span>
            ) : (
              'Update Now'
            )}
          </button>
        </div>
      </div>

      {/* Force update overlay to prevent interaction */}
      {forceUpdate && updating && (
        <div className="fixed inset-0 bg-gray-300 flex items-center justify-center z-[10000]">
          <div className="bg-slate-800 rounded-lg p-8 text-center">
            <svg className="animate-spin h-12 w-12 text-purple-500 mx-auto mb-4" viewBox="0 0 24 24">
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
                fill="none"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            <div className="text-gray-900 font-semibold">Updating Application...</div>
            <div className="text-gray-500 text-sm mt-2">Please wait, this will only take a moment</div>
          </div>
        </div>
      )}
    </div>
  );
}
